import { useCallback, useEffect, useState } from 'react';

import type { EmployeePort, MyEmployeeRecord, MyProfileUpdate } from './ports/employeePort';

/**
 * Loads and saves the signed-in employee's own record.
 *
 * The port decides what may be written; this only coordinates the async work
 * around it (§5.4).
 */
export interface MyRecordController {
  readonly record: MyEmployeeRecord | null;
  readonly loading: boolean;
  readonly errorAr: string | null;
  readonly savedAr: string | null;
  readonly save: (update: MyProfileUpdate) => Promise<void>;
}

export function useMyRecord(employees: EmployeePort, enabled: boolean): MyRecordController {
  const [record, setRecord] = useState<MyEmployeeRecord | null>(null);
  const [errorAr, setErrorAr] = useState<string | null>(null);
  const [savedAr, setSavedAr] = useState<string | null>(null);
  /** Bumped to re-read after a save. */
  const [reloadToken, setReloadToken] = useState(0);

  /*
   * Derived rather than stored.
   *
   * A stored `loading` flag has to be set as the effect runs, and can disagree
   * with the record it describes. "Enabled, nothing loaded, nothing failed"
   * *is* loading; there is no second fact to keep in step.
   */
  const loading = enabled && record === null && errorAr === null;

  useEffect(() => {
    if (!enabled) return;

    // `cancelled` rather than a ref: the cleanup runs when the effect is torn
    // down, which is exactly when a late reply should stop mattering.
    let cancelled = false;

    employees
      .getMyRecord()
      .then((next) => {
        if (cancelled) return;
        setRecord(next);
        setErrorAr(null);
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setErrorAr(error instanceof Error ? error.message : 'تعذّر تحميل بياناتك.');
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, employees, reloadToken]);

  const save = useCallback(
    async (update: MyProfileUpdate) => {
      setErrorAr(null);
      setSavedAr(null);
      try {
        await employees.updateMyProfile(update);
        // Re-read rather than patching local state: the stored record is the
        // truth, and a screen that trusts its own optimistic copy will
        // eventually show something the database does not agree with.
        setReloadToken((token) => token + 1);
        setSavedAr('تم حفظ بياناتك.');
      } catch (error) {
        setErrorAr(error instanceof Error ? error.message : 'تعذّر حفظ البيانات.');
      }
    },
    [employees],
  );

  return { record, loading, errorAr, savedAr, save };
}
